import { useIsOverQuota } from '@qse/stores'
import { toast } from '@qse/ui'
import { type FileItem } from '@qse/utils'
import { useEffect } from 'react'

interface UseFileExplorerKeyboardShortcutsProps {
  files: FileItem[]
  selectedFiles: Set<string | number>
  setSelectedFiles: (files: Set<string | number>) => void
  onBulkDelete: (selectedFileIds: Set<string | number>) => void
  onBulkDeleteForever: (selectedFileIds: Set<string | number>) => void
  onClipboardCopy: () => void
  onClipboardPaste: () => void
  onCreateFolder: () => void
  onRename: (fileId: string | number, path?: string) => void
  onSelectAll?: () => void
  parentType?: 'CHAT_ROOT' | 'CHAT_CONVERSATION'
  trashMode?: boolean
}

export const useFileExplorerKeyboardShortcuts = ({
  files,
  selectedFiles,
  setSelectedFiles,
  onBulkDelete,
  onBulkDeleteForever,
  onClipboardCopy,
  onClipboardPaste,
  onCreateFolder,
  onRename,
  onSelectAll,
  parentType,
  trashMode = false,
}: UseFileExplorerKeyboardShortcutsProps) => {
  const isOverQuota = useIsOverQuota()

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if the target is an input, textarea, or contenteditable element
      const target = e.target as HTMLElement

      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.contentEditable === 'true'
      ) {
        return // Don't handle shortcuts when typing in input fields
      }

      // Ctrl+A (Windows/Linux) or Cmd+A (Mac) for select all
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault()
        if (onSelectAll) {
          onSelectAll()
        } else {
          // Fallback to original behavior - filter out system folders
          const selectableFiles = files.filter(file => {
            if (file.systemFolderType === 'CHAT_ROOT' && !trashMode) {
              return false
            }

            return true
          })
          const allFileIds = new Set(selectableFiles.map(f => f.id))

          setSelectedFiles(allFileIds)
        }
      }

      // Ctrl+C (Windows/Linux) or Cmd+C (Mac) for copy selected files
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault()
        if (isOverQuota) {
          toast.error('Storage quota exceeded. Delete files to free up space.')

          return
        }
        onClipboardCopy()
      }

      // Ctrl+V (Windows/Linux) or Cmd+V (Mac) for paste files
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault()
        if (isOverQuota) {
          toast.error('Storage quota exceeded. Delete files to free up space.')

          return
        }
        // Prevent paste in CHAT_ROOT folders
        if (parentType) {
          toast.error('You cannot paste in system folders')

          return
        }
        onClipboardPaste()
      }

      // Ctrl+Shift+N for create new folder
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'N') {
        e.preventDefault()
        onCreateFolder()
      }

      // Delete key for deleting selected files
      if (e.key === 'Delete') {
        e.preventDefault()

        if (selectedFiles.size > 0) {
          // In trash mode, Delete key triggers permanent delete
          if (trashMode) {
            onBulkDeleteForever(selectedFiles)
          } else {
            onBulkDelete(selectedFiles)
          }
        }
      }

      // Shift+Delete for permanent delete (works in both normal and trash mode)
      if (e.shiftKey && e.key === 'Delete') {
        e.preventDefault()

        if (selectedFiles.size > 0) {
          onBulkDeleteForever(selectedFiles)
        }
      }

      // F2 key for renaming selected file
      if (e.key === 'F2') {
        e.preventDefault()

        if (selectedFiles.size === 1) {
          const selectedFileId = Array.from(selectedFiles)[0]
          const selectedFile = files.find(f => f.id === selectedFileId)

          if (selectedFile) {
            onRename(selectedFileId, selectedFile.path || '')
          }
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [
    files,
    selectedFiles,
    setSelectedFiles,
    onBulkDelete,
    onBulkDeleteForever,
    onClipboardCopy,
    onClipboardPaste,
    onCreateFolder,
    onRename,
    trashMode,
    isOverQuota,
  ])
}
